import {
    action,
    makeObservable,
    observable,
    runInAction,
    computed,
    reaction,
} from "mobx";
import { BaseStore } from "stores/BaseStore";
import type { IRootStore } from "stores/RootStore";
import { AcxStore } from "stores/RootStore";
import { AuthStore } from "stores/AuthStore";
import { v4 as uuidv4 } from "uuid";
import type {
    IntegrationType,
    IntegrationKeyFlags,
    IntegrationKeyValues,
} from "../types";
import { IntegrationService } from "services/IntegrationService";
import IntegrationConfiguration from "models/OrganizationModels/OrganizationIntegrationConfiguration";
import { DataHandlerStore } from "./DataHandlerStore";
import { isEqual } from "lodash";

@AcxStore
export class IntegrationStore extends BaseStore {
    readonly dataHandlerStore: DataHandlerStore;
    readonly authStore: AuthStore;

    @observable hasIntegrationKey: IntegrationKeyFlags = {};
    @observable integrationKeys: IntegrationKeyValues = {};
    @observable selectedIntegration: IntegrationType | undefined;
    @observable integrations: IntegrationType[] = [];
    @observable isCheckKeysLoading = false;
    @observable isLoading = true;
    @observable hasError = {
        amazonDbFields: false,
        amazonKeys: false,
    };
    @observable hasSelectedIntegration = false;

    private integrationService = new IntegrationService();

    constructor(private rootStore: IRootStore) {
        super("IntegrationStore");
        makeObservable(this);

        this.authStore = rootStore.getStore(AuthStore);
        this.dataHandlerStore = rootStore.getStore(DataHandlerStore);

        reaction(
            () => [this.selectedIntegration, this.integrationKeys],
            ([integration, keys], [prevIntegration, prevKeys]) => {
                // If there is no selected integration or there are unsaved changes, we don't need to check for changes
                if (
                    !this.hasSelectedIntegration ||
                    this.dataHandlerStore.hasUnsavedChanges ||
                    !this.dataHandlerStore.canEdit
                ) {
                    return;
                }

                if (integration !== prevIntegration || keys !== prevKeys) {
                    this.dataHandlerStore.setHasUnsavedChanges(true);
                }
            },
            { equals: isEqual },
        );
    }

    checkIfIntegrationIsValid = () => {
        const integration = this.selectedIntegration;
        const integrationKeys = this.integrationKeys;
        const hasIntegrationKey = this.hasIntegrationKey;

        let isValid = true;
        if (!integration) {
            this.hasError = {
                amazonDbFields: false,
                amazonKeys: false,
            };
            return isValid;
        }
        if (integration.type === "AmazonConnect") {
            if (
                !integration.url ||
                !integration.configuration?.customConfiguration?.BucketName ||
                !integration.configuration?.customConfiguration?.BucketRegion ||
                !integration.configuration?.customConfiguration?.InstanceId ||
                !integration.configuration?.customConfiguration
                    ?.InstanceRegion ||
                !integration.configuration?.customConfiguration
                    ?.AudioDirectoryName
            ) {
                this.hasError.amazonDbFields = true;
                isValid = false;
            } else {
                this.hasError.amazonDbFields = false;
            }
            // If the keys already exist, then we don't need to check for them
            // If one key is defined, the other should be too
            if (
                !hasIntegrationKey["amazon-access-key"] &&
                !hasIntegrationKey["amazon-secret-key"] &&
                ((integrationKeys.amazonAccessKey &&
                    !integrationKeys.amazonSecretKey) ||
                    (integrationKeys.amazonSecretKey &&
                        !integrationKeys.amazonAccessKey))
            ) {
                this.hasError.amazonKeys = true;
                isValid = false;
            } else {
                this.hasError.amazonKeys = false;
            }
        }
        return isValid;
    };

    @action
    updateSelectedIntegration = (integration: IntegrationType) => {
        this.selectedIntegration = integration;
        if (this.hasError.amazonDbFields || this.hasError.amazonKeys) {
            this.checkIfIntegrationIsValid();
        }
    };

    @action
    setSelectedIntegration = (integration: IntegrationType) => {
        this.hasSelectedIntegration = false;
        this.selectedIntegration = integration;
        this.checkKeys();

        setTimeout(() => {
            runInAction(() => {
                this.hasSelectedIntegration = true;
            });
        });
    };

    @action
    setIntegrationKeys = (keys: IntegrationKeyValues) => {
        this.integrationKeys = keys;
    };

    @computed
    get shownIntegrations() {
        return this.integrations.filter(
            (integration) => integration.type === "AmazonConnect",
        );
    }

    @action
    createIntegration = (selectedSource: string) => {
        if (selectedSource === "AmazonConnect") {
            const newIntegrationInfo: IntegrationType = {
                id: uuidv4(),
                isActive: false,
                lastUpdate: new Date().toISOString(),
                organizationId: this.authStore.activeOrgId || "",
                type: "AmazonConnect",
                url: "",
                configuration: {
                    clientSecretSuffix: "",
                    minimumCallLengthInSeconds: null,
                    maximumCallLengthInSeconds: null,
                    customConfiguration: {
                        AudioDirectoryName: "",
                        ChatDirectoryName: "",
                        BucketName: "",
                        BucketRegion: "",
                        InstanceAlias: "",
                        InstanceId: "",
                        InstanceRegion: "",
                        queueIds: [],
                        agentGroupingOptions: 1,
                    },
                },
            };
            this.integrationKeys = {
                amazonAccessKey: "",
                amazonSecretKey: "",
            };
            this.hasIntegrationKey = {
                amazonAccessKey: false,
                amazonSecretKey: false,
            };
            this.setSelectedIntegration(newIntegrationInfo);
        }
    };

    @action
    checkKeys = async () => {
        const integration = this.selectedIntegration;
        this.integrationKeys = {};
        this.isCheckKeysLoading = true;

        await this.setupAsyncTask("Check Integration Keys", async () => {
            const hasKeys = await this.integrationService.checkKeys(
                integration?.id || "",
            );

            runInAction(() => {
                const suffix = integration?.configuration?.clientSecretSuffix;
                // If there is a suffix, we need to remove it from the keys
                if (suffix) {
                    const updatedKeys = {};
                    Object.entries(hasKeys).forEach(([key, value]) => {
                        const keyWithoutSuffix = key.replace(`-${suffix}`, "");
                        updatedKeys[keyWithoutSuffix] = value;
                    });
                    this.hasIntegrationKey = updatedKeys;
                } else {
                    this.hasIntegrationKey = hasKeys;
                }
            });
        });

        this.isCheckKeysLoading = false;
    };

    @action
    loadData = async () => {
        this.isLoading = true;
        this.hasSelectedIntegration = false;
        await this.setupAsyncTask("Load Integrations", async () => {
            const integrations = await this.integrationService
                .getIntegrationConfiguration(this.authStore.activeOrgId || "")
                .catch(() => []);

            runInAction(() => {
                this.integrations = integrations;
            });
        });
        this.isLoading = false;
    };

    @action
    saveIntegration = async () => {
        const integrationInfo = this.selectedIntegration;

        if (
            !integrationInfo ||
            !this.checkIfIntegrationIsValid() ||
            !this.dataHandlerStore.canEdit
        ) {
            return;
        }

        try {
            let updatedIntegrations = [...this.integrations];

            const existingIntegration = this.integrations.find(
                (integration) => integration.id === integrationInfo?.id,
            );
            if (existingIntegration) {
                updatedIntegrations = this.integrations.map((integration) => {
                    if (integration.id === integrationInfo?.id) {
                        return integrationInfo;
                    }
                    return integration;
                });
            } else {
                updatedIntegrations.push(integrationInfo);
            }

            await this.setupAsyncTask("Save Integration", async () => {
                const updatedList =
                    await this.integrationService.updateIntegrationConfiguration(
                        updatedIntegrations as IntegrationConfiguration[],
                        this.authStore.activeOrgId || "",
                    );

                const newIntegration = updatedList.find(
                    (integration) =>
                        !this.integrations.some((i) => i.id === integration.id),
                );

                const integration = newIntegration || integrationInfo;

                const suffix = integration.configuration?.clientSecretSuffix;
                const keySuffix = suffix ? `-${suffix}` : "";

                const updatedIntegrationKeys = {
                    ...(this.integrationKeys.amazonAccessKey
                        ? {
                              [`amazon-access-key${keySuffix}`]:
                                  this.integrationKeys.amazonAccessKey,
                          }
                        : {}),
                    ...(this.integrationKeys.amazonSecretKey
                        ? {
                              [`amazon-secret-key${keySuffix}`]:
                                  this.integrationKeys.amazonSecretKey,
                          }
                        : {}),
                };

                if (Object.keys(updatedIntegrationKeys).length && integration) {
                    await this.integrationService.updateSecrets(
                        integration.id,
                        updatedIntegrationKeys,
                    );
                }
            });

            this.loadData();
            this.dataHandlerStore.setActiveStep(0);
        } catch (e) {
            console.error(e);
        }
    };

    @action
    deleteIntegrations = async () => {
        const selectedIntegrations =
            this.dataHandlerStore.dataGridStore.selectedRows;

        // Check if at least one item selected is an integration
        if (
            !selectedIntegrations.some((integration) => integration.type) ||
            !this.dataHandlerStore.canEdit
        ) {
            return;
        }

        this.isLoading = true;

        const updatedIntegrations = this.integrations.filter(
            (integration) =>
                !selectedIntegrations.some(
                    (selectedIntegration) =>
                        selectedIntegration.id === integration.id,
                ),
        );
        await this.integrationService.updateIntegrationConfiguration(
            updatedIntegrations as IntegrationConfiguration[],
            this.authStore.activeOrgId || "",
        );
        this.dataHandlerStore.dataGridStore.clearSelected();
        await this.loadData();
    };
}
